import React, { useState } from 'react';
import { BsArrowDown, BsArrowUp } from 'react-icons/bs';
import { format, parseISO } from 'date-fns';
import { usePatient } from 'hooks/patient';
import { MetricTypeEnum } from 'enums/MetricTypeEnum';
import {
  BloodPressureSummary,
  MetricExtreme,
  MetricSummary,
  ScalarMetricSummary,
} from 'entities/MetricSummary';
import { getMetricSummary } from 'services/metrics';
import formatNumber from 'utils/formatNumber';
import formatSleepMinutes from 'utils/formatSleepMinutes';
import { useCurrentEffect } from 'lib/use-current-effect';
import {
  LastMeasurement,
  MinMax,
  Container,
  LastRegister,
  Component,
  Note,
  When,
} from './styles';
import { DateRangeType } from '../Modals/EvolutionModal';

interface EvolutionMinMaxProps {
  metric: MetricTypeEnum;
  rangeOptions: DateRangeType;
  measure?: string;
}

type Status = 'loading' | 'ready' | 'empty' | 'failed';

/**
 * Os rótulos das métricas reduzidas por noite/dia (`per` do metrics). "Máx/Min" de
 * sono ou de passos não diria o que o número é: é a pior noite, o dia mais ativo.
 */
const PER_LABELS = {
  night: { min: 'Pior noite', max: 'Melhor noite', last: 'Última noite' },
  day: { min: 'Dia menos ativo', max: 'Dia mais ativo', last: 'Último dia' },
};

const isBloodPressure = (
  summary: MetricSummary,
): summary is BloodPressureSummary => 'systolic' in summary;

const dayOf = (extreme: MetricExtreme): string =>
  format(parseISO(extreme.mensuramentDate), 'dd/MM');

/**
 * Mínimo, máximo e último registro do período da evolução detalhada.
 *
 * ⚠️ **Os números vêm do `GET /metrics/summary`, calculados sobre TODAS as
 * leituras.** Até 2026-10-01 o quadro usava o `groupByDay`, que devolve só a última
 * leitura de cada dia: em "Hoje" mostrava mínimo = máximo = último, e nos períodos
 * longos perdia todo pico que não fosse o último do dia. O gráfico continua com um
 * ponto por dia na semana e no mês; o quadro é que diz o extremo real.
 *
 * Pressão **por componente**, em linhas separadas: os extremos da sistólica e da
 * diastólica podem vir de leituras diferentes, e juntá-los num par mostraria uma
 * pressão que nunca foi medida.
 *
 * Sono, passos e calorias vêm **reduzidos** pelo metrics (a última versão de cada
 * noite; o total de cada dia), com o dia do extremo ao lado — é ele que deixa a
 * enfermeira cruzar "a pior noite" com o resto do prontuário.
 */
export const EvolutionMinMax = ({
  metric,
  rangeOptions,
  measure,
}: EvolutionMinMaxProps) => {
  const [summary, setSummary] = useState<MetricSummary>();
  const [status, setStatus] = useState<Status>('loading');

  const { patient } = usePatient();
  const patientId = patient?.id;

  const { endDate, startDate } = rangeOptions;

  const bottomInformation =
    metric === MetricTypeEnum.BLOOD_PREASSURE ||
    metric === MetricTypeEnum.HEART_BEAT_RATE ||
    metric === MetricTypeEnum.RESPIRATORY_RATE ||
    metric === MetricTypeEnum.GLUCOSE ||
    metric === MetricTypeEnum.CALORIES;

  const shown = (extreme: MetricExtreme | null): string => {
    if (!extreme) return '—';
    if (metric === MetricTypeEnum.SLEEP)
      return formatSleepMinutes(extreme.value);
    // Contagens: sem casa decimal e com separador de milhar (9.800).
    if (metric === MetricTypeEnum.STEPS || metric === MetricTypeEnum.CALORIES) {
      return formatNumber(extreme.value, 0);
    }
    // Uma casa: a temperatura chega do sensor como float de 32 bits.
    return formatNumber(extreme.value, 1);
  };

  useCurrentEffect(
    (isCurrent) => {
      if (!patientId) {
        return;
      }

      setStatus('loading');

      getMetricSummary({
        patientId,
        metricType: metric,
        startDate,
        endDate,
      })
        .then((data) => {
          if (!isCurrent()) return;

          setSummary(data);
          setStatus(data.count > 0 ? 'ready' : 'empty');
        })
        .catch(() => {
          // Falha não some em silêncio: um quadro ausente se confundiria com
          // "sem leituras no período".
          if (isCurrent()) setStatus('failed');
        });
    },
    [patientId, metric, startDate, endDate],
  );

  if (status === 'loading' || status === 'empty') {
    return <Container key={metric} />;
  }

  if (status === 'failed' || !summary) {
    return (
      <Container key={metric}>
        <MinMax>
          <Note>Não foi possível calcular mínimo e máximo do período.</Note>
        </MinMax>
      </Container>
    );
  }

  const per = isBloodPressure(summary) ? undefined : summary.per;
  const labels = per ? PER_LABELS[per] : undefined;

  const lastMeasurement = isBloodPressure(summary)
    ? summary.last && `${summary.last.systolic}|${summary.last.diastolic}`
    : shown((summary as ScalarMetricSummary).last);

  const extremesBlock = () => {
    if (isBloodPressure(summary)) {
      return (
        <>
          <Component>Sistólica</Component>
          <span>
            <BsArrowUp color="#305BE5" height="16px" />
            Máx: <b>{shown(summary.systolic.max)}</b>
          </span>
          <span>
            <BsArrowDown color="#FF617D" height="16px" />
            Min: <b>{shown(summary.systolic.min)}</b>
          </span>
          <Component>Diastólica</Component>
          <span>
            <BsArrowUp color="#305BE5" height="16px" />
            Máx: <b>{shown(summary.diastolic.max)}</b>
          </span>
          <span>
            <BsArrowDown color="#FF617D" height="16px" />
            Min: <b>{shown(summary.diastolic.min)}</b>
          </span>
        </>
      );
    }

    if (labels) {
      // Uma noite / um dia só ("Hoje"): pior = melhor = último, e repetir o mesmo
      // número três vezes não informa nada.
      if (summary.count < 2) {
        return (
          <Note>
            {per === 'night' ? 'Uma noite no período.' : 'Um dia no período.'}
          </Note>
        );
      }

      // O rótulo numa linha própria, como "Sistólica" na pressão: "Dia menos ativo:
      // 10.646 (27/09)" numa linha só não cabe no quadro e espremia a seta.
      return (
        <>
          <Component>{labels.min}</Component>
          <span>
            <BsArrowDown color="#FF617D" height="16px" />
            <b>{shown(summary.min)}</b>
            {summary.min && <When>({dayOf(summary.min)})</When>}
          </span>
          <Component>{labels.max}</Component>
          <span>
            <BsArrowUp color="#305BE5" height="16px" />
            <b>{shown(summary.max)}</b>
            {summary.max && <When>({dayOf(summary.max)})</When>}
          </span>
        </>
      );
    }

    return (
      <>
        <span>
          <BsArrowUp color="#305BE5" height="16px" />
          Máx: <b>{shown(summary.max)}</b>
        </span>
        <span>
          <BsArrowDown color="#FF617D" height="16px" />
          Min: <b>{shown(summary.min)}</b>
        </span>
      </>
    );
  };

  return (
    <Container key={metric}>
      <MinMax>
        {extremesBlock()}
        {summary.truncated && (
          <Note>Parcial: nem todas as leituras do período foram lidas.</Note>
        )}
      </MinMax>
      <LastMeasurement>
        <strong />

        <LastRegister>
          <span>{labels ? labels.last : 'Último registro'}</span>
          <div>
            <h3>
              {lastMeasurement ?? '—'}
              {!bottomInformation && measure}
            </h3>
            {bottomInformation && <span>{measure}</span>}
          </div>
        </LastRegister>
      </LastMeasurement>
    </Container>
  );
};
